import objectKeysToCamelCase from '@grebban/utils/object/keysToCamelCase';
import { createSlice } from '@reduxjs/toolkit';
import resolveApplication from '@fogia/core-web/state/application/resolve';
import { camelCaseMapValues } from '@fogia/core-web/utils/camelCaseMapValues';
import { getLinkUrlFromLinkContent, type LinkContentLike } from '@fogia/core-web/utils/internalRefToSlug';

export interface IHeaderState {
    data: Record<string, any>;
}

/** Normalised submenu section for overlay (with or without title). */
export interface SubmenuSection {
    title?: string;
    columnLayout: string;
    items: Array<{ label: string; link?: { url: string; text?: string } } & Record<string, unknown>>;
}

/**
 * Normalises raw CMS submenu (array of menuSectionComponent) into SubmenuSection[].
 * Resolves linkType "internal" / "external" via _references so each item gets link.url when applicable.
 */
const normaliseSubmenu = (submenu: unknown, references: Record<string, unknown>): SubmenuSection[] => {
    if (submenu == null || !Array.isArray(submenu)) return [];
    return submenu.map((section: Record<string, unknown>) => {
        const items = Array.isArray(section.items) ? section.items : [];
        return {
            ...(section.title != null && section.title !== '' && { title: String(section.title) }),
            columnLayout: section.columnLayout != null ? String(section.columnLayout) : '1',
            items: items.map((item: Record<string, unknown>) => {
                const label = item.label != null ? String(item.label) : '';
                const content = item?.content as LinkContentLike | undefined;
                const url = getLinkUrlFromLinkContent(content, references);
                return {
                    ...item,
                    label,
                    ...(url != null && { link: { url, text: label } }),
                };
            }),
        };
    });
};

/**
 * Iterates over each menu item in mainMenu/utilityMenu: resolves link from content (internal/external)
 * and replaces content.submenu with normaliseSubmenu when submenu exists and is an array.
 */
const applySubmenuNormalisation = (menu: unknown, references: Record<string, unknown>): unknown[] => {
    if (!Array.isArray(menu)) return [];
    return menu.map((item) => {
        const content =
            item && typeof item === 'object' && 'content' in item
                ? (item as { content?: { submenu?: unknown } }).content
                : undefined;
        const submenu = content?.submenu;
        const refs = references ?? {};
        const normalised = submenu != null && Array.isArray(submenu) ? normaliseSubmenu(submenu, refs) : submenu;
        const url = content ? getLinkUrlFromLinkContent(content as LinkContentLike, refs) : undefined;
        const { label } = item as { label?: string };
        const link =
            url != null
                ? { url, text: label != null ? String(label) : '' }
                : (item as { link?: { url: string; text?: string } }).link;
        if (normalised !== submenu && content) {
            return { ...item, ...(link != null && { link }), content: { ...content, submenu: normalised } };
        }
        if (link != null) {
            return { ...item, link };
        }
        return item;
    });
};

const initialState: IHeaderState = {
    data: {},
};
const slice = createSlice({
    name: 'header',
    initialState,
    reducers: { setHeaderState: (state, { payload }) => ({ ...state, ...payload }) },
    extraReducers(builder) {
        builder.addCase(resolveApplication.fulfilled, (state, { payload }) => {
            const configHeader = objectKeysToCamelCase(payload?.data?.config?.header || {}, {
                recursive: true,
                modifyInputObject: false,
            });
            const references = camelCaseMapValues(
                (payload?.data?._references ?? {}) as Record<string, unknown>,
            ) as Record<string, unknown>;

            const navFromPayload = payload?.data?.settings?.headerNavigation;
            let headerNavigation: unknown = navFromPayload;
            if (navFromPayload != null && typeof navFromPayload === 'object') {
                const nav = objectKeysToCamelCase(navFromPayload as Record<string, unknown>, {
                    recursive: true,
                    modifyInputObject: false,
                });
                headerNavigation = {
                    ...nav,
                    ...(nav.mainMenu != null && {
                        mainMenu: applySubmenuNormalisation(nav.mainMenu, references),
                    }),
                    ...(nav.utilityMenu != null && {
                        utilityMenu: applySubmenuNormalisation(nav.utilityMenu, references),
                    }),
                };
            }

            const data = {
                ...configHeader,
                ...(headerNavigation != null && { headerNavigation }),
            };
            return !Object.keys(data).length ? state : { ...state, data };
        });
    },
});

export const { setHeaderState } = slice.actions;
export const { reducer } = slice;
